import { baseApi } from "../redux/baseAPI";

export const radiologyApi = baseApi.injectEndpoints({
  tagTypes: ["Radiology", "PendingRadiology"],
  endpoints: (builder) => ({
    getRadiology: builder.query({
      query: (body) => {
        return {
          url: `/radiology/orders.json`,
          method: "GET",
          params: body,
        };
      },
      providesTags: ["Radiology"],
      transformResponse: (response, meta, arg) => response.response.data,
    }),
    getFutureRadiology: builder.query({
      query: (body) => {
        return {
          url: `/radiology/orders.json`,
          method: "GET",
          params: { get_results: 0, e10x_labs: 0, future_orders: 1, ...body },
        };
      },
      providesTags: ["FutureRadiology"],
      transformResponse: (response, meta, arg) => response.response.data,
    }),

    getRadiologyCategory: builder.query({
      query: (body) => {
        return {
          url: `/radiology/categories.json`,
          method: "GET",
          params: { ...body },
        };
      },
      providesTags: ["radiologyCategory"],
      transformResponse: (response, meta, arg) => response?.response?.data,
    }),

    getRadiologyCategoryTestByCodes: builder.query({
      query: (body) => {
        return {
          url: `/radiology/codes_by_category.json`,
          method: "GET",
          params: { ...body },
        };
      },
      providesTags: ["radiologyCategoryCodes"],
      transformResponse: (response, meta, arg) => response?.response?.data,
    }),

    getPendingRadiology: builder.query({
      query: (body) => {
        return {
          url: `/radiology/orders.json`,
          method: "GET",
          params: { get_results: 0, e10x_labs: 0, ...body },
        };
      },
      providesTags: ["PendingRadiology"],
      transformResponse: (response, meta, arg) => response.response.data,
    }),
    getAwaitingResults: builder.query({
      query: (data) => {
        return {
          url: `/radiology/orders.json`,
          method: "GET",
          params: { get_results: 0, e10x_labs: 0, sent: 1, ...data },
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
      providesTags: ["LabAwaitingResults"],
    }),
    getRadiologyResult: builder.query({
      query: (body) => {
        return {
          url: `/radiology/orders.json`,
          method: "GET",
          params: { ...body },
        };
      },
      providesTags: ["Radiologyresult"],
      transformResponse: (response, meta, arg) => response?.response?.data,
    }),
    getRadiologyRefusal: builder.query({
      query: (patientId) => {
        return {
          url: `/refusals.json`,
          method: "GET",
          params: {
            pid: patientId,
            type: "radiology",
          },
        };
      },
      providesTags: ["RadiologyRefusal"],
      transformResponse: (response, meta, arg) => response.response.data,
    }),
    getRadiologySetting: builder.query({
      query: (patientId) => {
        return {
          url: `/radiology/settings.json`,
          method: "GET",
          params: {
            pid: patientId,
          },
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
    }),
    getRadiologyRefusalSetting: builder.query({
      query: (patientId) => {
        return {
          url: `/refusals/settings.json`,
          method: "GET",
          params: {
            pid: patientId,
          },
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
    }),
    getRadiologyDropDownSetting: builder.query({
      query: (patientId) => {
        return {
          url: `/providers/index.json`,
          method: "GET",
          params: {
            pid: patientId,
          },
        };
      },
      transformResponse: (response, meta, arg) => response.response.data,
    }),

    getRadiologySign: builder.mutation({
      query: (data) => ({
        url: `/radiology/sign.json`,
        method: "GET",
        params: {
          pid: data.pid,
          id: data.id,
          sign: 1,
        },
      }),
      invalidatesTags: ["Radiology"],
    }),
    getRadiologySend: builder.mutation({
      query: (data) => ({
        url: `/radiology/sign.json`,
        method: "GET",
        params: {
          pid: data.pid,
          id: data.id,
          send: 1,
          sign: 1,
        },
      }),
      invalidatesTags: ["Radiology"],
    }),

    getRadiologyUnSign: builder.mutation({
      query: (data) => {
        return {
          url: `/radiology/sign.json`,
          method: "GET",
          params: {
            pid: data.pid,
            id: data.id,
            sign: 0,
            skip_auto_accept: data?.skip_auto_accept === 0 ? 0 : 1,
          },
        }
      },
      invalidatesTags: ["Radiology"],
    }),
    getRadiologyDelete: builder.mutation({
      query: (body) => ({
        url: `/radiology/delete.json`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: ["Radiology"],
    }),
    getRadiologyOrderSearch: builder.query({
      query: (body) => ({
        url: `/radiology/orders.json`,
        method: "GET",
        params: body,
      }),
      invalidatesTags: ["Radiology"],
    }),
    getRadiologyDeleteRefusal: builder.mutation({
      query: (body) => ({
        url: `/refusals/delete.json`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: ["Radiology"],
    }),

    getRadiologyFileUid: builder.mutation({
      query: (body) => ({
        url: `/radiology/requisition_report.json`,
        method: "POST",
        params: body,
      }),
      transformResponse: (response, meta, arg) => response.response,
    }),
    getRadiologyFileUidResult: builder.mutation({
      query: (body) => ({
        url: `/radiology/result_report.json`,
        method: "POST",
        params: body,
      }),
      transformResponse: (response, meta, arg) => response.response.data,
    }),

    getRadiologySave: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/radiology/place_order.json`,
        method: "POST",
        data: body,
        params: { pid: body.pid },
      }),
      transformResponse: (response, meta, arg) => response.response,
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          radiologyApi.util.updateQueryData("getRadiology", id, (draft) => {
            Object.assign(draft, patch);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: "Radiology" }],
    }),

    getRadiologySaveResult: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/radiology/save_results.json`,
        method: "POST",
        data: body,
        params: { pid: body.pid },
      }),
      transformResponse: (response, meta, arg) =>
        response?.response?.data?.current,
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          radiologyApi.util.updateQueryData(
            "getRadiologyResult",
            id,
            (draft) => {
              Object.assign(draft, patch);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Radiologyresult" },
        { type: "LabAwaitingResults" },
        { type: "Radiology" },
        { type: "PendingRadiology" }
      ],
    }),
    getRadiologySaveRefusal: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/refusals/save.json`,
        method: "POST",
        data: body,
        params: { pid: body.pid },
      }),
      transformResponse: (response, meta, arg) =>
        response.response.data.current,
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          radiologyApi.util.updateQueryData(
            "getRadiologyResult",
            id,
            (draft) => {
              Object.assign(draft, patch);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Radiologyresult", id },
      ],
    }),
    getRadiologyLabFav: builder.mutation({
      query: (body) => ({
        url: `/labs/provider_lab_rad_fav_list.json`,
        method: "POST",
        params: body,
      }),
      transformResponse: (response, meta, arg) => response.response.data,
    }),
    getRadiologyLabAddFav: builder.mutation({
      query: (body) => ({
        url: `/labs/add_fav_lab_rad.json`,
        method: "POST",
        data: body,
      }),
      transformResponse: (response, meta, arg) => response.response.data,
    }),
    getRadiologyLabRecent: builder.mutation({
      query: (body) => ({
        url: `/labs/provider_most_recent_list.json`,
        method: "POST",
        params: body,
      }),
      transformResponse: (response, meta, arg) => response.response.data,
    }),
    getRadiologyAcknowledge: builder.mutation({
      query: (data) => ({
        url: `/radiology/acknowledge.json`,
        method: "GET",
        params: {
          pid: data.pid,
          radiology_id: data.id,
          sign: 0,
        },
      }),
      invalidatesTags: ["Radiologyresult"],
    }),
  }),
});

export const {
  useGetRadiologyQuery,
  useGetPendingRadiologyQuery,
  useGetAwaitingResultsQuery,
  useGetFutureRadiologyQuery,
  useGetRadiologyResultQuery,
  useGetRadiologyRefusalQuery,
  useGetRadiologySettingQuery,
  useGetRadiologyRefusalSettingQuery,
  useGetRadiologyDropDownSettingQuery,
  useGetRadiologySignMutation,
  useGetRadiologySendMutation,
  useGetRadiologyUnSignMutation,
  useGetRadiologyDeleteMutation,
  useGetRadiologyDeleteRefusalMutation,
  useGetRadiologySaveMutation,
  useGetRadiologyFileUidMutation,
  useGetRadiologyFileUidResultMutation,
  useGetRadiologySaveResultMutation,
  useGetRadiologySaveRefusalMutation,
  useGetRadiologyOrderSearchQuery,
  useGetRadiologyLabFavMutation,
  useGetRadiologyLabAddFavMutation,
  useGetRadiologyLabRecentMutation,
  useGetRadiologyAcknowledgeMutation,
} = radiologyApi;
